import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DrunkButton } from '../components/DrunkButton';
import { DrunkCard } from '../components/DrunkCard';
import { PinInput } from '../components/PinInput';
import { ModeSelector, MODES } from '../components/ModeSelector';
import { useSession } from '../context/SessionContext';
import { ArrowLeft, Copy, Check, PartyPopper } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const NewSession = () => {
  const navigate = useNavigate();
  const { saveSession } = useSession();
  
  const [step, setStep] = useState(1);
  const [mode, setMode] = useState('drunk_ai');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [pin, setPin] = useState('');
  const [setupAnswers, setSetupAnswers] = useState({});
  const [sessionData, setSessionData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  const isValidEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const selectedMode = MODES[mode];
  const setupQuestions = selectedMode?.setupQuestions || [];

  // Build setup context from answers
  const getSetupContext = () => {
    const answers = Object.entries(setupAnswers)
      .filter(([_, value]) => value && value.trim())
      .map(([index, value]) => `${setupQuestions[index]}: ${value}`)
      .join('\n');
    return answers || null;
  };

  const handleCreateSession = async () => {
    if (!name.trim()) {
      toast.error("What's your name?");
      return;
    }
    if (!isValidEmail(email)) {
      toast.error("Need a valid email to find you later!");
      return;
    }
    if (pin.length !== 4) {
      toast.error("PIN needs to be 4 digits!");
      return;
    }

    setIsLoading(true);
    try {
      const response = await axios.post(`${API}/sessions/create`, {
        creator_name: name.trim(),
        creator_email: email.trim().toLowerCase(),
        pin: pin,
        mode: mode,
        setup_context: getSetupContext()
      });
      
      setSessionData(response.data);
      saveSession({
        ...response.data,
        pin: pin,
        member_name: name.trim(),
        member_email: email.trim().toLowerCase(),
        mode: mode
      });
      setStep(6);
      toast.success(`${selectedMode.name} created! ${selectedMode.emoji}`);
    } catch (error) {
      const message = error.response?.data?.detail || "Something went wrong!";
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  const copySessionCode = () => {
    navigator.clipboard.writeText(sessionData?.session_code || '');
    setCopied(true);
    toast.success("Copied! Share with your friends!");
    setTimeout(() => setCopied(false), 2000);
  };

  const goToSession = () => {
    navigate(`/session/${sessionData.session_code}`);
  };

  return (
    <div className="min-h-screen bg-cream p-4">
      <div className="max-w-md mx-auto">
        {/* Back Button */}
        <DrunkButton
          variant="ghost"
          onClick={() => step > 1 ? setStep(step - 1) : navigate('/')}
          className="mb-4"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-5 h-5 inline mr-1" />
          {step > 1 ? 'Back' : 'Home'}
        </DrunkButton>

        {/* Step 1: Choose Mode */}
        {step === 1 && (
          <div className="bounce-in">
            <div className="text-center mb-6">
              <h2 className="font-heading text-2xl md:text-3xl font-bold mb-2">
                What's the Vibe? ✨
              </h2>
              <p className="font-body text-sm text-black/70">
                Choose the experience for your session
              </p>
            </div>
            
            <ModeSelector selectedMode={mode} onSelectMode={setMode} />
            
            <DrunkButton
              variant="primary"
              size="large"
              onClick={() => setStep(2)}
              className={`w-full py-4 mt-6 ${selectedMode.colors.primary}`}
              data-testid="next-mode-btn"
            >
              Continue with {selectedMode.name} {selectedMode.emoji}
            </DrunkButton>
          </div>
        )}

        {/* Step 2: Setup Questions */}
        {step === 2 && (
          <div className="bounce-in">
            <DrunkCard variant="default" className="p-5">
              <div className="text-center mb-4">
                <span className="text-4xl">{selectedMode.emoji}</span>
                <h2 className="font-heading text-xl md:text-2xl font-bold mt-2">
                  Tell me about it
                </h2>
                <p className="font-body text-sm text-black/70">
                  Helps personalize the experience
                </p>
              </div>
              
              <div className="space-y-4">
                {setupQuestions.map((question, index) => (
                  <div key={index}>
                    <label className="font-body text-sm font-bold block mb-1">
                      {question}
                    </label>
                    <input
                      type="text"
                      value={setupAnswers[index] || ''}
                      onChange={(e) => setSetupAnswers(prev => ({
                        ...prev,
                        [index]: e.target.value
                      }))}
                      className="w-full p-3 text-base border-2 border-black rounded-xl font-body shadow-brutal focus:outline-none focus:border-pink"
                      placeholder="Optional"
                      data-testid={`setup-question-${index}`}
                    />
                  </div>
                ))}
              </div>
              
              <div className="flex gap-2 mt-6">
                <DrunkButton
                  variant="ghost"
                  size="default"
                  onClick={() => setStep(3)}
                  className="flex-1"
                  data-testid="skip-setup-btn"
                >
                  Skip
                </DrunkButton>
                <DrunkButton
                  variant="primary"
                  size="large"
                  onClick={() => setStep(3)}
                  className={`flex-1 ${selectedMode.colors.primary}`}
                  data-testid="continue-setup-btn"
                >
                  Continue
                </DrunkButton>
              </div>
            </DrunkCard>
          </div>
        )}

        {/* Step 3: Enter Name */}
        {step === 3 && (
          <div className="bounce-in">
            <DrunkCard variant="default" className="text-center p-5">
              <span className="text-4xl mb-3 block">{selectedMode.emoji}</span>
              <h2 className="font-heading text-2xl md:text-3xl font-bold mb-4">
                What's Your Name?
              </h2>
              
              <input
                type="text"
                placeholder="Your name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full p-4 text-lg border-2 border-black rounded-xl mb-4 font-body shadow-brutal focus:outline-none focus:border-pink"
                maxLength={50}
                data-testid="name-input"
                autoComplete="off"
              />
              
              <DrunkButton
                variant="primary"
                size="large"
                onClick={() => setStep(4)}
                disabled={!name.trim()}
                className="w-full py-4"
                data-testid="next-step-btn"
              >
                Next 👉
              </DrunkButton>
            </DrunkCard>
          </div>
        )}

        {/* Step 4: Enter Email */}
        {step === 4 && (
          <div className="bounce-in">
            <DrunkCard variant="default" className="text-center p-5">
              <h2 className="font-heading text-2xl md:text-3xl font-bold mb-2">
                Your Email 📧
              </h2>
              <p className="font-body text-sm mb-4 text-black/70">
                So you can rejoin if you drop out
              </p>
              
              <input
                type="email"
                placeholder="you@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full p-4 text-lg border-2 border-black rounded-xl mb-4 font-body shadow-brutal focus:outline-none focus:border-pink"
                maxLength={100}
                data-testid="email-input"
                autoComplete="email"
              />
              
              <DrunkButton
                variant="cyan"
                size="large"
                onClick={() => setStep(5)}
                disabled={!email.trim() || !isValidEmail(email)}
                className="w-full py-4"
                data-testid="next-email-btn"
              >
                Next: Set PIN 🔐
              </DrunkButton>
            </DrunkCard>
          </div>
        )}

        {/* Step 5: Enter PIN */}
        {step === 5 && (
          <div className="bounce-in">
            <DrunkCard variant="default" className="text-center p-5">
              <h2 className="font-heading text-2xl md:text-3xl font-bold mb-2">
                Set a 4-Digit PIN 🔒
              </h2>
              <p className="font-body text-sm mb-6 text-black/70">
                Others will need this to join
              </p>
              
              <div className="mb-6">
                <PinInput
                  value={pin}
                  onChange={setPin}
                  length={4}
                />
              </div>
              
              <DrunkButton
                variant="accent"
                size="large"
                onClick={handleCreateSession}
                disabled={pin.length !== 4 || isLoading}
                className="w-full py-4"
                data-testid="create-session-btn"
              >
                {isLoading ? 'Creating...' : `Create ${selectedMode.name}! ${selectedMode.emoji}`}
              </DrunkButton>
            </DrunkCard>
          </div>
        )}

        {/* Step 6: Session Created */}
        {step === 6 && sessionData && (
          <div className="bounce-in">
            <DrunkCard variant="gradient" className={`text-center text-white p-5 bg-gradient-to-br ${selectedMode.colors.gradient}`}>
              <PartyPopper className="w-12 h-12 mx-auto mb-3" />
              <h2 className="font-heading text-2xl md:text-3xl font-bold mb-4">
                {selectedMode.name} Created!
              </h2>
              
              <p className="font-body text-sm mb-3">
                Share this code:
              </p>
              
              <div 
                className="session-code inline-block cursor-pointer hover:scale-105 transition-transform mb-4 text-xl"
                onClick={copySessionCode}
                data-testid="session-code-display"
              >
                {sessionData.session_code}
                {copied ? (
                  <Check className="w-5 h-5 inline ml-2" />
                ) : (
                  <Copy className="w-5 h-5 inline ml-2" />
                )}
              </div>
              
              <div className="bg-white/20 rounded-xl p-4 mb-4 border-2 border-white/40">
                <p className="font-bold text-sm mb-2">🧠 REMEMBER THIS:</p>
                <div className="space-y-1 text-sm">
                  <p className="font-mono">Code: <span className="bg-white text-black px-2 py-0.5 rounded">{sessionData.session_code}</span></p>
                  <p className="font-mono">PIN: <span className="bg-white text-black px-2 py-0.5 rounded">{pin}</span></p>
                </div>
                <p className="text-xs mt-2 opacity-80">📸 Screenshot this!</p>
              </div>
              
              <DrunkButton
                variant="secondary"
                size="large"
                onClick={goToSession}
                className="w-full py-4"
                data-testid="enter-session-btn"
              >
                Let's Go! {selectedMode.emoji}
              </DrunkButton>
            </DrunkCard>
          </div>
        )}
      </div>
    </div>
  );
};

export default NewSession;
